import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, ChevronLeft, ChevronRight,
  Clock, Video, Phone, Mail, FileText, RefreshCw, CheckCircle,
  User, Briefcase, ArrowRight, ExternalLink,
} from 'lucide-react';
import { calendarAPI } from '../services/api';
import './CalendarWidget.css';

// ── Constants ─────────────────────────────────────────────────

const DAYS_FR   = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = [
  'Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre',
];

const EVENT_CFG = {
  AO_DEADLINE:   { color:'#DC2626', bg:'rgba(239,68,68,0.12)', border:'rgba(239,68,68,0.22)', label:'Date limite',    Icon: Clock         },
  ENTRETIEN:     { color:'#22C55E', bg:'rgba(34,197,94,0.08)', border:'rgba(34,197,94,0.22)', label:'Entretien',      Icon: Video         },
  APPEL:         { color:'#0891B2', bg:'rgba(8,145,178,0.10)', border:'rgba(8,145,178,0.28)', label:'Appel',          Icon: Phone         },
  EMAIL:         { color:'var(--text-muted)', bg:'#F9FAFB', border:'var(--border)', label:'Email',          Icon: Mail          },
  NOTE:          { color:'#7C3AED', bg:'rgba(139,92,246,0.10)', border:'rgba(139,92,246,0.28)', label:'Note',           Icon: FileText      },
  RELANCE:       { color:'#F59E0B', bg:'#FFFBEB', border:'#FDE68A', label:'Relance',        Icon: RefreshCw     },
  RETOUR_CLIENT: { color:'#059669', bg:'#ECFDF5', border:'rgba(34,197,94,0.25)', label:'Retour client',  Icon: CheckCircle   },
};

const PRIORITY_CFG = {
  URGENT: { label:'Urgent',        bg:'rgba(239,68,68,0.12)', color:'#DC2626' },
  HIGH:   { label:'Priorité haute',bg:'#FFF7ED', color:'#F97316' },
};

const HOUR_START = 7;
const HOUR_END   = 21;
const HOURS      = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => i + HOUR_START);

// ── Date helpers ──────────────────────────────────────────────

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth()    === b.getMonth()    &&
    a.getDate()     === b.getDate()
  );
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function startOfWeek(d) {
  const r   = new Date(d);
  const dow = r.getDay(); // 0=Sun
  r.setDate(r.getDate() + (dow === 0 ? -6 : 1 - dow));
  return r;
}

function buildMonthGrid(year, month) {
  const first  = new Date(year, month, 1);
  const last   = new Date(year, month + 1, 0);
  let   offset = first.getDay(); // 0=Sun
  offset = offset === 0 ? 6 : offset - 1; // Mon-first

  const cells = [];
  for (let i = offset - 1; i >= 0; i--) {
    cells.push({ date: new Date(year, month, -i), current: false });
  }
  for (let d = 1; d <= last.getDate(); d++) {
    cells.push({ date: new Date(year, month, d), current: true });
  }
  while (cells.length < 42) {
    cells.push({ date: new Date(year, month + 1, cells.length - offset - last.getDate() + 1), current: false });
  }
  return cells;
}

function fmtTime(dt) {
  const d = new Date(dt);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function fmtDateFull(dt) {
  const d   = new Date(dt);
  const dow = DAYS_FR[(d.getDay() + 6) % 7];
  return `${dow} ${d.getDate()} ${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

function eventsOnDay(events, date) {
  return events.filter(e => sameDay(new Date(e.start), date));
}

function cfg(type) {
  return EVENT_CFG[type] || { color:'var(--text-muted)', bg:'#F9FAFB', border:'var(--border)', label: type, Icon: Calendar };
}

// ── EventPill (month cell) ────────────────────────────────────

function EventPill({ event, onClick }) {
  const c = cfg(event.event_type);
  return (
    <div
      className={`cal-pill${event.is_done ? ' done' : ''}${event.is_past && !event.is_done ? ' past' : ''}${event.priority === 'URGENT' ? ' urgent' : ''}`}
      style={{ background: c.bg, color: c.color, borderColor: c.border }}
      onClick={e => { e.stopPropagation(); onClick(event, e); }}
      title={event.title}
    >
      {event.priority === 'URGENT' && <span className="cal-pill-dot pulsing" style={{ background: c.color }} />}
      <span className="cal-pill-text">{event.title}</span>
    </div>
  );
}

// ── EventTooltip ──────────────────────────────────────────────

function EventTooltip({ event, anchor, onClose, onNavigate }) {
  const ref = useRef();
  const c   = cfg(event.event_type);
  const { Icon } = c;
  const prio = PRIORITY_CFG[event.priority];

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Smart position: avoid viewport overflow
  const style = {};
  if (anchor) {
    const vw  = window.innerWidth;
    const vh  = window.innerHeight;
    let left  = anchor.x + anchor.width / 2 - 160;
    let top   = anchor.bottom + 8;
    if (left + 320 > vw - 16) left = vw - 336;
    if (left < 16)             left = 16;
    if (top  + 360 > vh - 16)  top  = anchor.top - 368;
    style.left = left;
    style.top  = Math.max(8, top);
  }

  const start = new Date(event.start);
  const end   = event.end ? new Date(event.end) : null;
  const canNav = event.ao_id || event.pipeline_entry_id;

  return (
    <div className="cal-tooltip" style={style} ref={ref}>
      {/* Header */}
      <div className="cal-tt-header" style={{ borderBottomColor: c.color + '33' }}>
        <span className="cal-tt-type" style={{ background: c.bg, color: c.color }}>
          <Icon size={11} /> {c.label}
        </span>
        {prio && (
          <span className="cal-tt-prio" style={{ background: prio.bg, color: prio.color }}>
            {prio.label}
          </span>
        )}
        {event.is_done && <span className="cal-tt-done">Fait</span>}
        <button className="cal-tt-close" onClick={onClose}>×</button>
      </div>

      {/* Title */}
      <div className="cal-tt-title">{event.title}</div>

      {/* Meta rows */}
      <div className="cal-tt-meta">
        <div className="cal-tt-row">
          <Calendar size={12} className="cal-tt-icon" />
          <span>{fmtDateFull(start)}</span>
          {!event.all_day && (
            <strong className="cal-tt-time">
              {fmtTime(start)}{end ? ` → ${fmtTime(end)}` : ''}
            </strong>
          )}
        </div>

        {event.candidate_name && (
          <div className="cal-tt-row">
            <User size={12} className="cal-tt-icon" />
            <span>{event.candidate_name}</span>
          </div>
        )}

        {(event.ao_title || event.ao_client) && (
          <div className="cal-tt-row">
            <Briefcase size={12} className="cal-tt-icon" />
            <span>
              {event.ao_title}
              {event.ao_client && ` — ${event.ao_client}`}
            </span>
          </div>
        )}

        {event.duration_minutes && (
          <div className="cal-tt-row">
            <Clock size={12} className="cal-tt-icon" />
            <span>{event.duration_minutes} min</span>
          </div>
        )}
      </div>

      {/* Teams link */}
      {event.teams_link && (
        <a
          href={event.teams_link}
          target="_blank"
          rel="noreferrer"
          className="cal-tt-teams"
          onClick={e => e.stopPropagation()}
        >
          <Video size={12} /> Rejoindre la réunion Teams
          <ExternalLink size={11} style={{ marginLeft:'auto' }} />
        </a>
      )}

      {/* Description */}
      {event.description && (
        <div className="cal-tt-desc">
          {event.description.length > 160
            ? event.description.slice(0, 157) + '…'
            : event.description}
        </div>
      )}

      {/* Navigate */}
      {canNav && (
        <button className="cal-tt-nav" style={{ background: c.color }} onClick={onNavigate}>
          Voir le détail <ArrowRight size={12} />
        </button>
      )}
    </div>
  );
}

// ── Month View ────────────────────────────────────────────────

function MonthView({ events, date, today, onEventClick }) {
  const year  = date.getFullYear();
  const month = date.getMonth();
  const cells = buildMonthGrid(year, month);
  const MAX   = 20; // scroll interne — on affiche tout

  return (
    <div className="cal-month">
      <div className="cal-month-headers">
        {DAYS_FR.map(d => <div key={d} className="cal-month-head">{d}</div>)}
      </div>
      <div className="cal-month-grid">
        {cells.map(({ date: d, current }, i) => {
          const dayEvs = eventsOnDay(events, d);
          const isToday = sameDay(d, today);
          const isPast  = d < today && !isToday;
          const visible = dayEvs.slice(0, MAX);
          const more    = dayEvs.length - MAX;

          return (
            <div
              key={i}
              className={[
                'cal-day-cell',
                !current ? 'other-month' : '',
                isToday  ? 'is-today'    : '',
                isPast   ? 'is-past'     : '',
              ].filter(Boolean).join(' ')}
            >
              <div className={`cal-day-num${isToday ? ' today-circle' : ''}`}>
                {d.getDate()}
              </div>
              <div className="cal-day-events">
                {visible.map(ev => (
                  <EventPill key={ev.id} event={ev} onClick={onEventClick} />
                ))}
                {more > 0 && (
                  <div className="cal-more-pill">+{more} autre{more > 1 ? 's' : ''}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Week View ─────────────────────────────────────────────────

function WeekView({ events, date, today, onEventClick }) {
  const ws      = startOfWeek(date);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  const totalMin = (HOUR_END - HOUR_START) * 60;

  const timedFor  = d => events.filter(e => !e.all_day && sameDay(new Date(e.start), d));
  const allDayFor = d => events.filter(e => e.all_day  && sameDay(new Date(e.start), d));

  return (
    <div className="cal-week">
      {/* All-day strip */}
      <div className="cal-week-allday-row">
        <div className="cal-week-gutter"><span className="cal-gutter-label">Journée</span></div>
        {weekDays.map((d, i) => (
          <div key={i} className={`cal-week-allday-col${sameDay(d, today) ? ' is-today' : ''}`}>
            {allDayFor(d).map(ev => <EventPill key={ev.id} event={ev} onClick={onEventClick} />)}
          </div>
        ))}
      </div>

      {/* Day headers */}
      <div className="cal-week-head-row">
        <div className="cal-week-gutter" />
        {weekDays.map((d, i) => (
          <div key={i} className={`cal-week-day-head${sameDay(d, today) ? ' is-today' : ''}`}>
            <span className="cal-wday-name">{DAYS_FR[i]}</span>
            <span className={`cal-wday-num${sameDay(d, today) ? ' today-circle' : ''}`}>
              {d.getDate()}
            </span>
          </div>
        ))}
      </div>

      {/* Time grid */}
      <div className="cal-week-body">
        {/* Hour labels */}
        <div className="cal-week-gutter cal-week-hours">
          {HOURS.map(h => (
            <div key={h} className="cal-hour-label">{h}h</div>
          ))}
        </div>

        {/* Day columns */}
        <div className="cal-week-cols">
          {weekDays.map((d, di) => {
            const dayEvs = timedFor(d);
            const isToday = sameDay(d, today);

            return (
              <div key={di} className={`cal-week-col${isToday ? ' is-today' : ''}`}>
                {HOURS.map(h => <div key={h} className="cal-hour-slot" />)}

                {dayEvs.map(ev => {
                  const s    = new Date(ev.start);
                  const dur  = ev.duration_minutes || (ev.event_type === 'ENTRETIEN' ? 60 : 30);
                  const top  = Math.max(0, ((s.getHours() - HOUR_START) * 60 + s.getMinutes()) / totalMin * 100);
                  const h    = Math.max(2.5, (dur / totalMin) * 100);
                  const c    = cfg(ev.event_type);
                  return (
                    <div
                      key={ev.id}
                      className={`cal-week-event${ev.is_done ? ' done' : ''}${ev.is_past && !ev.is_done ? ' past' : ''}`}
                      style={{ top:`${top}%`, height:`${h}%`, background: c.bg, borderLeftColor: c.color, color: c.color }}
                      onClick={e => { e.stopPropagation(); onEventClick(ev, e); }}
                    >
                      <div className="cal-we-title">{ev.title}</div>
                      {dur >= 40 && <div className="cal-we-time">{fmtTime(s)}</div>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Agenda View ───────────────────────────────────────────────

function AgendaView({ events, today, onEventClick }) {
  const cutoff = addDays(today, -7);
  const sorted = [...events]
    .filter(e => new Date(e.start) >= cutoff)
    .sort((a, b) => new Date(a.start) - new Date(b.start));

  // Group by date key
  const groups = [];
  let lastKey = null;
  for (const ev of sorted) {
    const d   = new Date(ev.start);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (key !== lastKey) {
      lastKey = key;
      groups.push({ date: d, evs: [] });
    }
    groups[groups.length - 1].evs.push(ev);
  }

  if (groups.length === 0) {
    return (
      <div className="cal-empty">
        <Calendar size={36} strokeWidth={1.3} />
        <p>Aucun événement dans les 90 prochains jours</p>
      </div>
    );
  }

  return (
    <div className="cal-agenda">
      {groups.map(({ date: d, evs }, gi) => {
        const isToday = sameDay(d, today);
        const isPast  = d < today && !isToday;
        const dow     = DAYS_FR[(d.getDay() + 6) % 7];
        return (
          <div key={gi} className="cal-agenda-group">
            <div className={`cal-agenda-date${isToday ? ' is-today' : ''}${isPast ? ' is-past' : ''}`}>
              {isToday
                ? <span className="cal-ag-today-label">Aujourd'hui</span>
                : <span className="cal-ag-dow">{dow}</span>
              }
              <span className="cal-ag-num">{d.getDate()}</span>
              <span className="cal-ag-mon">{MONTHS_FR[d.getMonth()].slice(0,3)}</span>
            </div>

            <div className="cal-agenda-events">
              {evs.map(ev => {
                const c = cfg(ev.event_type);
                const { Icon } = c;
                const prio = PRIORITY_CFG[ev.priority];
                return (
                  <div
                    key={ev.id}
                    className={`cal-ag-event${ev.is_done ? ' done' : ''}${ev.is_past && !ev.is_done ? ' past' : ''}`}
                    style={{ borderLeftColor: c.color }}
                    onClick={e => onEventClick(ev, e)}
                  >
                    <div className="cal-ag-event-body">
                      <div className="cal-ag-top">
                        <span className="cal-ag-type" style={{ background: c.bg, color: c.color }}>
                          <Icon size={10} /> {c.label}
                        </span>
                        {prio && (
                          <span className="cal-ag-prio" style={{ background: prio.bg, color: prio.color }}>
                            {prio.label}
                          </span>
                        )}
                        {ev.is_done && <span className="cal-ag-done-badge">Fait</span>}
                      </div>
                      <div className="cal-ag-title">{ev.title}</div>
                      {(ev.candidate_name || ev.ao_title) && (
                        <div className="cal-ag-meta">
                          {ev.candidate_name && <span>{ev.candidate_name}</span>}
                          {ev.ao_title && <span className="cal-ag-sep">·</span>}
                          {ev.ao_title && <span>{ev.ao_title}{ev.ao_client ? ` — ${ev.ao_client}` : ''}</span>}
                        </div>
                      )}
                    </div>

                    <div className="cal-ag-right">
                      {!ev.all_day && (
                        <span className="cal-ag-time">{fmtTime(new Date(ev.start))}</span>
                      )}
                      {ev.all_day && <span className="cal-ag-allday">Journée</span>}
                      {ev.teams_link && (
                        <a
                          href={ev.teams_link}
                          target="_blank"
                          rel="noreferrer"
                          className="cal-ag-teams"
                          onClick={e => e.stopPropagation()}
                        >
                          Teams
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── CalendarWidget (main) ────────────────────────────────────

export default function CalendarWidget() {
  const navigate = useNavigate();
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [view,    setView]    = useState('month');
  const [date,    setDate]    = useState(new Date());
  const [tooltip, setTooltip] = useState(null);   // { event, anchor }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await calendarAPI.getEvents();
        setEvents(res.data || []);
      } catch {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const prev = () => {
    const d = new Date(date);
    if (view === 'month') d.setMonth(d.getMonth() - 1);
    else                  d.setDate(d.getDate() - 7);
    setDate(d);
  };

  const next = () => {
    const d = new Date(date);
    if (view === 'month') d.setMonth(d.getMonth() + 1);
    else                  d.setDate(d.getDate() + 7);
    setDate(d);
  };

  const handleEventClick = (event, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ event, anchor: rect });
  };

  const handleNavigate = () => {
    if (!tooltip) return;
    const { event } = tooltip;
    setTooltip(null);
    if (event.ao_id)              navigate(`/ao/${event.ao_id}`);
    else if (event.pipeline_entry_id) navigate(`/pipeline/${event.pipeline_entry_id}`);
  };

  const headerLabel = () => {
    if (view === 'agenda') return 'Agenda';
    if (view === 'month')  return `${MONTHS_FR[date.getMonth()]} ${date.getFullYear()}`;
    const ws = startOfWeek(date);
    const we = addDays(ws, 6);
    const same = ws.getMonth() === we.getMonth();
    return same
      ? `${ws.getDate()} – ${we.getDate()} ${MONTHS_FR[ws.getMonth()]} ${ws.getFullYear()}`
      : `${ws.getDate()} ${MONTHS_FR[ws.getMonth()].slice(0,3)} – ${we.getDate()} ${MONTHS_FR[we.getMonth()].slice(0,3)} ${ws.getFullYear()}`;
  };

  const urgentCount = events.filter(e => e.priority === 'URGENT' && !e.is_done && !e.is_past).length;
  const upcomingCount = events.filter(e => !e.is_past && !e.is_done).length;

  return (
    <div className="cal-container">
      {/* ── Header ── */}
      <div className="cal-header">
        <div className="cal-header-left">
          <Calendar size={16} className="cal-header-icon" />
          <span className="cal-header-title">Calendrier</span>
          {urgentCount > 0 && (
            <span className="cal-urgent-badge">{urgentCount} urgent{urgentCount > 1 ? 's' : ''}</span>
          )}
          {upcomingCount > 0 && urgentCount === 0 && (
            <span className="cal-count-badge">{upcomingCount}</span>
          )}
        </div>

        <div className="cal-header-nav">
          <button className="cal-nav-btn" onClick={prev} aria-label="Précédent">
            <ChevronLeft size={15} />
          </button>
          <span className="cal-nav-label">{headerLabel()}</span>
          <button className="cal-nav-btn" onClick={next} aria-label="Suivant">
            <ChevronRight size={15} />
          </button>
          <button className="cal-today-btn" onClick={() => setDate(new Date())}>
            Aujourd'hui
          </button>
        </div>

        <div className="cal-view-switch">
          {[
            { k:'month',  l:'Mois'    },
            { k:'week',   l:'Semaine' },
            { k:'agenda', l:'Agenda'  },
          ].map(({ k, l }) => (
            <button
              key={k}
              className={`cal-view-btn${view === k ? ' active' : ''}`}
              onClick={() => setView(k)}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* ── Legend ── */}
      <div className="cal-legend">
        {Object.entries(EVENT_CFG).map(([type, c]) => (
          <span key={type} className="cal-legend-item">
            <span className="cal-legend-dot" style={{ background: c.color }} />
            {c.label}
          </span>
        ))}
      </div>

      {/* ── Body ── */}
      {loading ? (
        <div className="cal-loading">
          <div className="spinner" /> Chargement des événements…
        </div>
      ) : (
        <>
          {view === 'month'  && <MonthView  events={events} date={date} today={today} onEventClick={handleEventClick} />}
          {view === 'week'   && <WeekView   events={events} date={date} today={today} onEventClick={handleEventClick} />}
          {view === 'agenda' && <AgendaView events={events} today={today} onEventClick={handleEventClick} />}
        </>
      )}

      {/* ── Tooltip ── */}
      {tooltip && (
        <EventTooltip
          event={tooltip.event}
          anchor={tooltip.anchor}
          onClose={() => setTooltip(null)}
          onNavigate={handleNavigate}
        />
      )}
    </div>
  );
}
